import moment from 'moment';
import { useRouter } from 'next/navigation';
import { useAccount } from 'wagmi';

import { Loader } from '@/components/Loader';
import { useDisplayLTV } from '@/hooks/loans/useDisplayLTV';
import { useAppSelector } from '@/hooks/redux';
import { useSortRequest } from '@/hooks/useSortRequest';
import { fetchLentLoans } from '@/redux/loans/loansSlice';
import { displayRepay } from '@/utils/formatter';
import getDueIn from '@/utils/getDueIn';
import { TLoanTableHeaders } from 'types/loan';

import CryptoHeadButton from '../../crypto-prices/CryptoHeadButton';
import LoanItem from '../LoanMobileGrid';
import MyLoansTabTable from './Table';

const columns: TLoanTableHeaders = [
  {
    Header: (props) => <CryptoHeadButton columnName='Assets' {...props} />,
    accessor: 'collection',
    sortId: 'tokenId',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Status' {...props} />,
    accessor: 'status',
    disableSortBy: true,
    thClassName: 'w-28 px-2',
    thPClassName: 'mx-auto',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Principal' {...props} />,
    accessor: 'amount',
    disableSortBy: true,
    sortId: 'principal',
    thClassName: 'w-28 px-2',
    thPClassName: 'mx-auto',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='APR' {...props} />,
    accessor: 'apr',
    disableSortBy: true,
    thClassName: 'w-24 px-2',
    thPClassName: 'mx-auto',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='LTV' disableSorting {...props} />,
    accessor: 'ltv',
    disableSortBy: true,
    thClassName: 'w-24 px-2',
    thPClassName: 'mx-auto',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Repay' {...props} />,
    accessor: 'repay',
    disableSortBy: true,
    thClassName: 'w-28 px-2',
    thPClassName: 'mx-auto',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Due in' {...props} />,
    accessor: 'due',
    disableSortBy: true,
    sortId: 'endDate',
    thClassName: 'w-28 px-2',
    thPClassName: 'mx-auto',
  },
  {
    accessor: 'more',
    thClassName: 'w-8',
    thPClassName: 'mx-auto',
  },
];

const LentTab: React.FC = () => {
  const { lentLoans, lentLoansLoading } = useAppSelector((state) => state.loans);
  const { address } = useAccount();
  const displayLTV = useDisplayLTV();
  const router = useRouter();

  useSortRequest(
    (props) => {
      if (!address) return null;

      return fetchLentLoans({ ...props, address });
    },
    [address]
  );

  const handleNavigate = (id: string | number) => router.push(`/loans/${id}/active`);

  return lentLoansLoading ? (
    <Loader className='pb-4' />
  ) : (
    lentLoans && (
      <>
        <div className='hidden h-full lg:block'>
          <MyLoansTabTable isLent columns={columns} data={lentLoans} wrapperStyle='h-full' thStyle='bg-white dark:bg-jet-black pb-4' tdStyle='bg-white-100 dark:bg-jet-black' />
        </div>
        <div className='flex flex-col my-3 lg:hidden'>
          {lentLoans.map((loan) => {
            const { id, loanRequest, status } = loan;
            const { apr, collection, principal, repay, tokenId, token, loanTokenOwner, expiryDate } = loanRequest;

            const expirationDate = moment(expiryDate);
            const isUnpaid = status === 'active' && expirationDate.isSameOrBefore(moment());
            const dueIn = getDueIn(expiryDate);

            const info = [
              {
                title: 'Loan',
                value: `${principal} ${token?.symbol}`,
              },
              {
                title: 'APR',
                value: `${apr}%`,
              },
              {
                title: 'LTV',
                value: displayLTV(principal, loanTokenOwner ? loanTokenOwner?.floorPrice : collection?.floorPrice, token?.symbol),
              },
              {
                title: 'Repay',
                value: `${Number(displayRepay(repay).toFixed(3))} ${token?.symbol}`,
              },
              {
                title: 'Due in',
                value: `${status === 'active' ? dueIn : '---'}`,
              },
            ];

            return <LoanItem handle={handleNavigate} uniqueId={id} imageUrl={loanTokenOwner?.image || collection?.image || '/bayc-collectors.png'} key={id} id={tokenId} name={loanTokenOwner ? loanTokenOwner.collectionName : 'Collection Offer'} details={info} status={status} isUnpaid={isUnpaid} />;
          })}
        </div>
      </>
    )
  );
};

export default LentTab;
